import { UserIcon, UsersIcon, BarChartIcon } from "lucide-react";
import type { LucideIcon } from "lucide-react";

/**
 * Plan and term metadata for the sign-up wizard. Amounts are not defined here:
 * they come from Stripe through the `planPrices` query (entities/plan-price).
 */

export type PlanId = "start" | "grow" | "scale";

/** Matches the backend `BillingTerm` enum values. */
export type TermId = "monthly" | "annual" | "2y" | "3y";

export const PLAN_ORDER: PlanId[] = ["start", "grow", "scale"];

export type PlanMeta = {
  id: PlanId;
  name: string;
  icon: LucideIcon;
  popular: boolean;
  userCountKey: string;
  /** One-line pitch shown under the price in the plan column header. */
  descriptionKey: string;
  featureKeys: string[];
};

export const PLAN_META: Record<PlanId, PlanMeta> = {
  start: {
    id: "start",
    name: "Start",
    icon: UserIcon,
    popular: false,
    userCountKey: "1 user",
    descriptionKey: "Everything one user needs to find and win tenders.",
    featureKeys: [
      "Current tenders from 15+ sources",
      "AI chat documents",
      "Tender management",
      "SSO & MFA",
    ],
  },
  grow: {
    id: "grow",
    name: "Grow",
    icon: UsersIcon,
    popular: true,
    userCountKey: "1 user",
    descriptionKey:
      "Collaboration, integrations and profiles for growing teams.",
    featureKeys: [
      "Includes everything in Start",
      "Access to all tenders since 2017",
      "Bid agenda",
      "Microsoft Teams and Slack integration",
    ],
  },
  scale: {
    id: "scale",
    name: "Scale",
    icon: BarChartIcon,
    popular: false,
    userCountKey: "1 user",
    descriptionKey: "Advanced AI intelligence and forecasting for large teams.",
    featureKeys: [
      "Includes everything in Grow",
      "More than 3,000 sources unlocked via TenderApp Horizon",
      "AI tender comparison",
      "CRM integrations",
    ],
  },
};

export type ComparisonValue = boolean | string;

export type ComparisonRow = {
  /** Translation key for the row label. */
  labelKey: string;
  /** Per-plan cell: true = included, false = not included, string = text value. */
  values: Record<PlanId, ComparisonValue>;
};

export type ComparisonSection = {
  titleKey: string;
  rows: ComparisonRow[];
};

/**
 * Feature comparison across plans, grouped into the sections the tarieven page
 * on the marketing site uses. That page is the source for which plan carries
 * which feature — the two may differ on price, and on the seat counts, which
 * are the platform's; on features they may not.
 */
export const PLAN_COMPARISON: ComparisonSection[] = [
  {
    titleKey: "Signalling & insight",
    rows: [
      {
        labelKey: "Users",
        values: {
          start: "1 user",
          grow: "1 user",
          scale: "1 user",
        },
      },
      {
        labelKey: "Current tenders from 15+ sources",
        values: { start: true, grow: true, scale: true },
      },
      {
        labelKey: "Enriched tender insights by data team",
        values: { start: true, grow: true, scale: true },
      },
      {
        labelKey: "Smart notifications",
        values: { start: true, grow: true, scale: true },
      },
      {
        labelKey: "Access to all tenders since 2017",
        values: { start: false, grow: true, scale: true },
      },
      {
        labelKey: "Smart insights at advisor and authority level",
        values: { start: false, grow: true, scale: true },
      },
      {
        labelKey: "Add your own opportunities",
        values: { start: false, grow: false, scale: true },
      },
      {
        labelKey: "More than 3,000 sources unlocked via TenderApp Horizon",
        values: { start: false, grow: false, scale: true },
      },
      {
        labelKey: "Contract timeline",
        values: { start: false, grow: false, scale: true },
      },
    ],
  },
  {
    titleKey: "Qualifying",
    rows: [
      {
        labelKey: "AI chat documents",
        values: { start: true, grow: true, scale: true },
      },
      {
        labelKey: "AI tender comparison",
        values: { start: false, grow: false, scale: true },
      },
      {
        labelKey: "AI bid/no-bid advice (coming soon)",
        values: { start: false, grow: false, scale: true },
      },
    ],
  },
  {
    titleKey: "Bid management",
    rows: [
      {
        labelKey: "Tender management",
        values: { start: true, grow: true, scale: true },
      },
      {
        labelKey: "Notes & tasks",
        values: { start: true, grow: true, scale: true },
      },
      {
        labelKey: "Bid agenda",
        values: { start: false, grow: true, scale: true },
      },
    ],
  },
  {
    titleKey: "Forecasting",
    rows: [
      {
        labelKey: "Contract expiry & forecast",
        values: { start: false, grow: false, scale: true },
      },
    ],
  },
  {
    titleKey: "Management & integrations",
    rows: [
      {
        labelKey: "SSO & MFA",
        values: { start: true, grow: true, scale: true },
      },
      {
        labelKey: "Data export",
        values: { start: true, grow: true, scale: true },
      },
      {
        labelKey: "User profiles",
        values: { start: false, grow: true, scale: true },
      },
      {
        labelKey: "Personal and shared views",
        values: { start: false, grow: true, scale: true },
      },
      {
        labelKey: "Microsoft Teams and Slack integration",
        values: { start: false, grow: true, scale: true },
      },
      {
        labelKey: "CRM integrations",
        values: { start: false, grow: false, scale: true },
      },
    ],
  },
];

/** Committed years per term. Monthly has no fixed term (0). */
export const TERM_YEARS: Record<TermId, number> = {
  monthly: 0,
  annual: 1,
  "2y": 2,
  "3y": 3,
};

export const formatEur = (amount: number, locale: string): string =>
  `€${amount.toLocaleString(locale)}`;

/**
 * Dutch standard VAT rate. Prices are net (VAT-exclusive); 21% is added on top
 * at checkout unless the customer is VAT-exempt via reverse charge. This mirrors
 * the Stripe `tax_behavior=exclusive` prices — presentation only; Stripe Tax
 * makes the definitive determination.
 */
export const VAT_RATE = 0.21;

/** VAT amount (whole euros) added on top of a net amount. */
export const getVatAmount = (net: number): number => Math.round(net * VAT_RATE);

/** Gross amount = net + VAT (whole euros). */
export const getGrossAmount = (net: number): number => net + getVatAmount(net);

/** Terms offered per plan. Grow and Scale exclude monthly (it renders blocked). */
export const availableTerms = (plan: PlanId): TermId[] =>
  plan === "start" ? ["monthly", "annual"] : ["monthly", "annual", "2y", "3y"];

export const isTermBlocked = (plan: PlanId, term: TermId): boolean =>
  plan !== "start" && term === "monthly";

/** Start opens on annual; Grow and Scale open on the 2-year term. */
export const defaultTerm = (plan: PlanId): TermId =>
  plan === "start" ? "annual" : "2y";

export const isMonthlyBilled = (plan: PlanId, term: TermId): boolean =>
  plan === "start" && term === "monthly";

/**
 * Plans a customer can take on their own: a trial during registration, or the
 * plan button in the upgrade dialog and on the upgrade page. Only Start is
 * self-serve; Grow and Scale are quote-only (#3606) and show Contact Sales.
 * The backend enforces the same rule in `SubscriptionPlan::isSelfServe()`
 * (checkout) and in the Stripe sync; keep both in step.
 */
export const isSelfServePlan = (plan: PlanId): boolean => plan === "start";

/** Terms offered during registration — only Start's terms (#3606). */
export const REGISTRATION_TERMS: TermId[] = availableTerms("start");

/** Where quote-only plans (Grow/Scale) send the user to request a quote. */
export const QUOTE_URL = "https://www.tender.app/offerte-aanvragen";
